import { useMutation, useQueryClient } from '@tanstack/react-query';
import { can, formatCode } from '@pl/domain';
import type { OrderDetailDto } from '@pl/domain';
import * as RxDialog from '@radix-ui/react-dialog';
import { useState } from 'react';
import { Link } from 'react-router';
import { api } from '../../lib/api';
import { CHANNEL_DOT, channelName, shortDateTime, stateWords } from '../../lib/format';
import { qk, useMe, useOrder, useSiteMap, useSkuMap } from '../../lib/queries';
import { Bar, Chip, ErrorNote, RefetchNote, SiteTag, SkuTag, Spinner } from '../../ui/bits';
import { Icon } from '../../ui/Icon';
import { Drawer, Modal } from '../../ui/Dialog';
import { orderRef, progTone, shipByLabel, statusChip } from './Orders';
import s from './Orders.module.css';

export function OrderDrawer({ id, onClose }: { id: string | undefined; onClose: () => void }) {
  const orderQuery = useOrder(id);
  const me = useMe();
  const siteMap = useSiteMap();
  const skuMap = useSkuMap();
  const qc = useQueryClient();
  const [cancelOpen, setCancelOpen] = useState(false);
  const [reason, setReason] = useState('');

  const cancel = useMutation({
    mutationFn: (body: { reason: string }) => api.post<OrderDetailDto>(`/orders/${id}/cancel`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orders'] });
      if (id) qc.invalidateQueries({ queryKey: qk.order(id) });
      setCancelOpen(false);
      setReason('');
    },
  });

  const order = orderQuery.data;
  const ref = order ? orderRef(order) : '';
  const site = order ? siteMap.get(order.siteId) : undefined;
  const role = me.data?.role;
  const hasOrdersPerm = Boolean(role && can(role, 'orders'));
  const isOpenOrPicking = order ? order.status === 'open' || order.status === 'picking' : false;
  const canCancel = hasOrdersPerm && isOpenOrPicking;

  function closeButton() {
    return (
      <RxDialog.Close asChild>
        <button type="button" className="ico-btn" aria-label="Close">
          <Icon name="x" />
        </button>
      </RxDialog.Close>
    );
  }

  return (
    <>
      <Drawer
        open={Boolean(id)}
        onOpenChange={(next) => {
          if (!next) onClose();
        }}
        title={ref || 'Order'}
        header={
          <div className="drawer-h">
            {order ? (
              <>
                <div className={s.dTitle}>
                  <span className="mono">{ref}</span>
                  {statusChip(order)}
                  <span className="grow" />
                  {closeButton()}
                </div>
                <div className={s.dMeta}>
                  <span className="channel">
                    <i style={{ background: CHANNEL_DOT[order.channel] }} />
                    {channelName(order.channel)}
                  </span>
                  <span className={s.sepd} />
                  <b style={{ fontWeight: 600, color: 'var(--ink)' }}>{order.customerName}</b>
                  <span className={s.sepd} />
                  <SiteTag site={site} />
                  <span>Ship by {shipByLabel(order.requiredBy).text}</span>
                </div>
                <div className={s.dProg}>
                  <Bar value={order.scanned} max={order.totalQty} tone={progTone(order)} />
                  <span>
                    {order.scanned} / {order.totalQty} boxes
                  </span>
                </div>
              </>
            ) : (
              <div className={s.dTitle}>
                <span className="mono">Order</span>
                <span className="grow" />
                {closeButton()}
              </div>
            )}
          </div>
        }
        footer={
          order ? (
            <>
              <button type="button" className="btn danger sm" disabled={!canCancel} onClick={() => setCancelOpen(true)}>
                <Icon name="x" />
                Cancel order
              </button>
              {!hasOrdersPerm && (
                <span className="lead">
                  <Icon name="lock" />
                  Office only
                </span>
              )}
              <button type="button" className="btn sm off" disabled title="Lead only · on the phone. Partial shipping happens in Scan.">
                <Icon name="lock" />
                Ship partial
                <span className="lead" style={{ marginLeft: 2 }}>
                  Lead only
                </span>
              </button>
            </>
          ) : undefined
        }
      >
        {orderQuery.isPending ? (
          <div className="row">
            <Spinner label="Loading order" />
          </div>
        ) : orderQuery.isLoadingError ? (
          <div className="drawer-s stack" style={{ gap: 14, alignItems: 'flex-start' }}>
            <ErrorNote error={orderQuery.error} />
            <button type="button" className="btn" onClick={() => orderQuery.refetch()}>
              Try again
            </button>
          </div>
        ) : order ? (
          <>
            <RefetchNote queries={[orderQuery]} className="drawer-s" />
            <div className="drawer-s">
              <div className={s.sh}>
                <span className="sec-k">Lines</span>
                <span className="grow" />
                <span className="sub">
                  {order.lines.length} line{order.lines.length === 1 ? '' : 's'} · {order.totalQty} boxes
                </span>
              </div>
              {order.lines.map((line) => {
                const sku = skuMap.get(line.skuId);
                const scanned = line.picked + line.shipped;
                const done = line.remaining === 0 && line.qty > 0;
                return (
                  <div className={s.ln} key={line.id}>
                    <SkuTag sku={sku} withCode />
                    <span className="row" style={{ gap: 8 }}>
                      <span className={s.qty}>
                        {scanned}
                        <span className="faint"> / {line.qty}</span>
                      </span>
                      {done ? (
                        <Chip tone="ok">
                          <Icon name="check" size={11} />
                          Done
                        </Chip>
                      ) : (
                        <span className="faint" style={{ fontSize: 11 }}>
                          {line.shipped} shipped · {line.remaining} left
                        </span>
                      )}
                    </span>
                    <Bar value={scanned} max={line.qty} tone={done ? 'ok' : scanned > 0 ? 'accent' : undefined} />
                  </div>
                );
              })}
            </div>

            <div className="drawer-s">
              <div className={s.sh}>
                <span className="sec-k">Scanned boxes</span>
                <Chip>{order.boxes.length}</Chip>
                <span className="grow" />
              </div>
              {order.boxes.length === 0 ? (
                <p className="muted">No boxes scanned yet.</p>
              ) : (
                <ul className={s.scans}>
                  {order.boxes.map((b) => (
                    <li key={b.code}>
                      <time>{shortDateTime(b.updatedAt, site?.timezone)}</time>
                      <Link className="mono" to={`/office/boxes/${b.code}`}>
                        {formatCode(b.code)}
                      </Link>
                      <SkuTag sku={skuMap.get(b.skuId ?? '')} short />
                      <span className={s.st}>{stateWords(b.state)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="drawer-s">
              <div className={s.sh}>
                <span className="sec-k">Details</span>
              </div>
              <div className={s.dfacts}>
                <div>
                  <span className={s.k}>Created</span>
                  <span className={s.v}>{shortDateTime(order.createdAt, site?.timezone)}</span>
                </div>
                <div>
                  <span className={s.k}>Required by</span>
                  <span className={s.v}>{shipByLabel(order.requiredBy).text}</span>
                </div>
                <div>
                  <span className={s.k}>Notes</span>
                  <span className={s.v}>{order.notes ?? '—'}</span>
                </div>
                <div>
                  <span className={s.k}>Carrier</span>
                  <span className={s.v}>{order.carrier ?? '—'}</span>
                </div>
                <div>
                  <span className={s.k}>Tracking</span>
                  <span className={s.v}>{order.tracking ?? '—'}</span>
                </div>
              </div>
            </div>
          </>
        ) : null}
      </Drawer>

      <Modal
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        title="Cancel order"
        description={`${ref} will be cancelled and any picked boxes returned to stock.`}
        footer={
          <>
            <button type="button" className="btn" onClick={() => setCancelOpen(false)}>
              Keep order
            </button>
            <button type="button" className="btn primary" disabled={cancel.isPending || !reason.trim()} onClick={() => cancel.mutate({ reason: reason.trim() })}>
              Cancel order and return boxes
            </button>
          </>
        }
      >
        {cancel.error && <ErrorNote error={cancel.error} />}
        <label className="fld">
          <span>Reason</span>
          <textarea className="inp" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} />
        </label>
      </Modal>
    </>
  );
}
